import React, { useState, useEffect } from 'react';
import {
  XMarkIcon,
  BuildingOfficeIcon,
  CogIcon,
  ChartBarIcon,
  UsersIcon,
  ShieldExclamationIcon,
  ExclamationTriangleIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';
import FeatureManagement from './FeatureManagement';

const TenantDetails = ({ tenant, onClose }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [tenantDetails, setTenantDetails] = useState(null);
  const [showInactivateModal, setShowInactivateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [inactivateReason, setInactivateReason] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [operationLoading, setOperationLoading] = useState(false);

  useEffect(() => {
    if (tenant) {
      fetchTenantDetails();
    }
  }, [tenant]);

  const fetchTenantDetails = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/platform/tenants/${tenant.id}/details`);
      setTenantDetails(response.data);
    } catch (error) {
      console.error('Error fetching tenant details:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!tenant) return null;

  const handleInactivateOrganization = async () => {
    setOperationLoading(true);
    try {
      const response = await axios.put(`/api/platform/tenants/${tenant.id}/status`, {
        isActive: !tenant.status === 'ACTIVE',
        reason: inactivateReason
      });
      
      if (response.data.success) {
        alert(`Organization ${tenant.status === 'ACTIVE' ? 'deactivated' : 'activated'} successfully!`);
        setShowInactivateModal(false);
        setInactivateReason('');
        onClose(); // Close modal and refresh parent
      }
    } catch (error) {
      console.error('Error updating organization status:', error);
      alert(error.response?.data?.message || 'Failed to update organization status');
    } finally {
      setOperationLoading(false);
    }
  };

  const handleDeleteOrganization = async () => {
    setOperationLoading(true);
    try {
      const response = await axios.delete(`/api/platform/tenants/${tenant.id}`, {
        data: {
          confirmationText: deleteConfirmation,
          reason: deleteReason
        }
      });
      
      if (response.data.success) {
        alert(`Organization "${tenant.name}" has been permanently deleted!`);
        setShowDeleteModal(false);
        setDeleteReason('');
        setDeleteConfirmation('');
        onClose(); // Close modal and refresh parent
      }
    } catch (error) {
      console.error('Error deleting organization:', error);
      alert(error.response?.data?.message || 'Failed to delete organization');
    } finally {
      setOperationLoading(false);
    }
  };

  const tabs = [
    { id: 'overview', name: 'Overview', icon: ChartBarIcon },
    { id: 'features', name: 'Feature Management', icon: CogIcon },
    { id: 'users', name: 'Users', icon: UsersIcon },
    { id: 'management', name: 'Account Management', icon: ShieldExclamationIcon }
  ];

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-6xl shadow-lg rounded-lg bg-white">
        {/* Header */}
        <div className="flex items-center justify-between border-b pb-4 mb-4">
          <div className="flex items-center space-x-3">
            <BuildingOfficeIcon className="h-8 w-8 text-indigo-600" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{tenant.name}</h2>
              <p className="text-sm text-gray-500">
                {tenant.subdomain}.condor.com • {tenant.status}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-4">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-5 w-5 mr-2" />
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Content */}
        <div className="min-h-[400px] max-h-[600px] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-indigo-600 border-t-transparent"></div>
              <span className="ml-3 text-gray-600">Loading tenant details...</span>
            </div>
          ) : (
            <>
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  {/* Quick Stats */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-indigo-600">
                        {tenant.metrics?.totalUsers || 0}
                      </div>
                      <div className="text-sm text-gray-500">Total Users</div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">
                        {tenant.metrics?.activeUsersToday || 0}
                      </div>
                      <div className="text-sm text-gray-500">Active Today</div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-blue-600">
                        {tenant.metrics?.activeReservations || 0}
                      </div>
                      <div className="text-sm text-gray-500">Active Reservations</div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-purple-600">
                        {tenant.metrics?.healthScore || 0}%
                      </div>
                      <div className="text-sm text-gray-500">Health Score</div>
                    </div>
                  </div>

                  {/* Organization Info */}
                  <div className="bg-white border rounded-lg p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Organization Information</h3>
                    <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Organization ID</dt>
                        <dd className="mt-1 text-sm text-gray-900 font-mono">{tenant.id}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Created</dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {new Date(tenant.createdAt).toLocaleDateString()}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Subdomain</dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          <a 
                            href={`https://${tenant.subdomain}.condrs.com`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-indigo-600 hover:text-indigo-800 underline"
                          >
                            {tenant.subdomain}.condrs.com
                          </a>
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Status</dt>
                        <dd className="mt-1">
                          <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                            tenant.status === 'ACTIVE' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {tenant.status}
                          </span>
                        </dd>
                      </div>
                    </dl>
                  </div>

                  {/* Health Issues */}
                  {tenant.metrics?.healthIssues && tenant.metrics.healthIssues.length > 0 && (
                    <div className="bg-white border border-red-200 rounded-lg p-6">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Health Issues</h3>
                      <div className="space-y-3">
                        {tenant.metrics.healthIssues.map((issue, index) => (
                          <div 
                            key={index} 
                            className={`p-3 rounded-lg ${
                              issue.severity === 'high' 
                                ? 'bg-red-50 border border-red-200' 
                                : issue.severity === 'medium'
                                ? 'bg-yellow-50 border border-yellow-200'
                                : 'bg-gray-50 border border-gray-200'
                            }`}
                          >
                            <div className="flex items-start">
                              <div className="flex-1">
                                <div className="font-medium text-gray-900">{issue.message}</div>
                                <div className="text-sm text-gray-600 mt-1">{issue.description}</div>
                              </div>
                              <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                                issue.severity === 'high' 
                                  ? 'bg-red-100 text-red-800' 
                                  : issue.severity === 'medium'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {issue.severity}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Features Tab */}
              {activeTab === 'features' && (
                <FeatureManagement 
                  tenantId={tenant.id} 
                  tenantName={tenant.name}
                />
              )}

              {/* Users Tab */}
              {activeTab === 'users' && (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900">User Management</h3>
                  {tenantDetails?.users && tenantDetails.users.length > 0 ? (
                    <div className="border rounded-lg overflow-hidden">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Name
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Email
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Username
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Role
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Status
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Last Login
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {tenantDetails.users.map((user) => (
                            <tr key={user.id}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {`${user.firstName} ${user.lastName}`}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {user.email}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {user.username}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                <span className="inline-flex px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                                  {user.role}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                                  user.isActive 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {user.isActive ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {user.lastLoginAt 
                                  ? new Date(user.lastLoginAt).toLocaleDateString() 
                                  : 'Never'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      No users found for this organization
                    </div>
                  )}
                </div>
              )}

              {/* Account Management Tab */}
              {activeTab === 'management' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-medium text-gray-900">Account Management</h3>
                  
                  {/* Organization Status Section */}
                  <div className="bg-white border rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h4 className="text-md font-medium text-gray-900">Organization Status</h4>
                        <p className="text-sm text-gray-500">
                          Current status: 
                          <span className={`ml-2 inline-flex px-2 py-1 text-xs rounded-full ${
                            tenant.status === 'ACTIVE' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {tenant.status}
                          </span>
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex space-x-3">
                      {tenant.status === 'ACTIVE' ? (
                        <button
                          onClick={() => setShowInactivateModal(true)}
                          className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg transition-colors flex items-center"
                          disabled={operationLoading}
                        >
                          <ExclamationTriangleIcon className="h-4 w-4 mr-2" />
                          {operationLoading ? 'Processing...' : 'Inactivate Organization'}
                        </button>
                      ) : (
                        <button
                          onClick={() => setShowInactivateModal(true)}
                          className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center"
                          disabled={operationLoading}
                        >
                          <ExclamationTriangleIcon className="h-4 w-4 mr-2" />
                          {operationLoading ? 'Processing...' : 'Activate Organization'}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Danger Zone Section */}
                  <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                    <div className="flex items-center mb-4">
                      <ExclamationTriangleIcon className="h-6 w-6 text-red-600 mr-3" />
                      <div>
                        <h4 className="text-md font-medium text-red-900">Danger Zone</h4>
                        <p className="text-sm text-red-700">
                          Permanent deletion cannot be undone. All data will be lost forever.
                        </p>
                      </div>
                    </div>
                    
                    <button
                      onClick={() => setShowDeleteModal(true)}
                      className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center"
                      disabled={operationLoading}
                    >
                      <TrashIcon className="h-4 w-4 mr-2" />
                      {operationLoading ? 'Processing...' : 'Delete Organization'}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end mt-6 pt-4 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>

      {/* Inactivate/Activate Modal */}
      {showInactivateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative p-5 border w-96 shadow-lg rounded-lg bg-white">
            <div className="flex items-center justify-between border-b pb-3 mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {tenant.status === 'ACTIVE' ? 'Inactivate Organization' : 'Activate Organization'}
              </h3>
              <button
                onClick={() => {
                  setShowInactivateModal(false);
                  setInactivateReason('');
                }}
                className="text-gray-400 hover:text-gray-600"
                disabled={operationLoading}
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="mb-4">
              {tenant.status === 'ACTIVE' ? (
                <div className="flex items-start">
                  <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-gray-700 mb-2">
                      <strong>Warning:</strong> Inactivating this organization will:
                    </p>
                    <ul className="text-sm text-gray-600 space-y-1 ml-4">
                      <li>• Prevent all users from logging in</li>
                      <li>• Disable access to the business application</li>
                      <li>• Preserve all data (can be reactivated later)</li>
                    </ul>
                  </div>
                </div>
              ) : (
                <div className="flex items-start">
                  <ExclamationTriangleIcon className="h-5 w-5 text-green-600 mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-gray-700 mb-2">
                      Activating this organization will restore full access.
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason {tenant.status === 'ACTIVE' ? '(required)' : '(optional)'}
              </label>
              <textarea
                value={inactivateReason}
                onChange={(e) => setInactivateReason(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                rows="3"
                placeholder={`Please provide a reason for ${tenant.status === 'ACTIVE' ? 'inactivating' : 'activating'} this organization...`}
                disabled={operationLoading}
              />
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowInactivateModal(false);
                  setInactivateReason('');
                }}
                className="flex-1 px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-lg transition-colors"
                disabled={operationLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleInactivateOrganization}
                className={`flex-1 px-4 py-2 text-white rounded-lg transition-colors ${
                  tenant.status === 'ACTIVE'
                    ? 'bg-yellow-600 hover:bg-yellow-700'
                    : 'bg-green-600 hover:bg-green-700'
                }`}
                disabled={operationLoading || (tenant.status === 'ACTIVE' && !inactivateReason.trim())}
              >
                {operationLoading ? 'Processing...' : (tenant.status === 'ACTIVE' ? 'Inactivate' : 'Activate')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative p-5 border w-96 shadow-lg rounded-lg bg-white">
            <div className="flex items-center justify-between border-b pb-3 mb-4">
              <h3 className="text-lg font-medium text-red-900">Delete Organization</h3>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteReason('');
                  setDeleteConfirmation('');
                }}
                className="text-gray-400 hover:text-gray-600"
                disabled={operationLoading}
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="mb-4">
              <div className="flex items-start">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-600 mt-1 mr-3 flex-shrink-0" />
                <div>
                  <p className="text-sm text-red-900 font-semibold mb-2">
                    DANGER: This action cannot be undone!
                  </p>
                  <p className="text-sm text-gray-700 mb-2">
                    Deleting this organization will permanently remove:
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1 ml-4">
                    <li>• All users and their data</li>
                    <li>• All customers and reservations</li>
                    <li>• All inventory items and history</li>
                    <li>• All payments and financial records</li>
                    <li>• All settings and configurations</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for deletion (required)
              </label>
              <textarea
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                rows="3"
                placeholder="Please provide a detailed reason for deleting this organization..."
                disabled={operationLoading}
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type the organization name to confirm: <strong>{tenant.name}</strong>
              </label>
              <input
                type="text"
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                placeholder={tenant.name}
                disabled={operationLoading}
              />
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteReason('');
                  setDeleteConfirmation('');
                }}
                className="flex-1 px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-lg transition-colors"
                disabled={operationLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteOrganization}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                disabled={operationLoading || !deleteReason.trim() || deleteConfirmation !== tenant.name}
              >
                {operationLoading ? 'Deleting...' : 'Delete Organization'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantDetails;